import type { StepWithValidation } from '@/hooks/useStepsWithValidation'

/**
 * A file's processing state, resolved once so the File Status badge, the "Done Adding
 * Files" gate and the change-primary picker cannot describe the same file differently.
 *
 * Three copies of this ladder had drifted: the block reason ranked a failed transcode
 * above `inProcess`, the badge and the picker ranked it below, and `hasError` at the row
 * actions and in DeliveryModeSection left transcode failures out of "failed" entirely —
 * which quietly exempted the one failure mode only media files have from decision 9.
 */
export type StepStatus = 'failed' | 'analyzing' | 'preparing' | 'transcodeFailed' | 'unreviewed' | 'checking' | 'ready'

export function getStepStatus(step: StepWithValidation): StepStatus {
  if (step.processProgressError) return 'failed'
  // A re-upload over a failed transcode is working, not failed: the flags come from
  // different queries, so the previous attempt's result can still be sitting on the step.
  if (step.inProcess) return 'analyzing'
  if (step.transcodeCompleted === false) return 'preparing'
  if (step.transcodeSuccess === false) return 'transcodeFailed'
  if (!step.valid) return 'unreviewed'
  // Nothing known about the file yet, shown as "Checking…". Counting it ready would let
  // a still-processing file through.
  if (step.statusPending) return 'checking'
  return 'ready'
}

/**
 * Why a file is not yet ready to feed module generation. `null` means ready.
 *
 * Decision 15 blocks "Done Adding Files" until every file is ready, so this has to
 * agree with the badge the expert is looking at — a file showing Error while the button
 * stays enabled, or the reverse, is worse than either rule on its own.
 */
export type StepBlockReason = 'failed' | 'working' | 'unreviewed' | 'unknown'

const BLOCK_REASONS: Record<StepStatus, StepBlockReason | null> = {
  failed: 'failed',
  transcodeFailed: 'failed',
  analyzing: 'working',
  preparing: 'working',
  unreviewed: 'unreviewed',
  // Not 'working': a status that never arrived is unknown, and saying "still processing"
  // about a finished file is what a failed `/cm/steps_content` used to claim.
  checking: 'unknown',
  ready: null,
}

export function getStepBlockReason(step: StepWithValidation): StepBlockReason | null {
  return BLOCK_REASONS[getStepStatus(step)]
}

/** Whether the file has to be replaced rather than waited out. */
export function hasStepFailed(step: StepWithValidation): boolean {
  return getStepBlockReason(step) === 'failed'
}

export type StepReadiness = {
  failed: number
  working: number
  unreviewed: number
  /** Files whose status never arrived. Blocks, but for a different reason than `working`. */
  unknown: number
  /** Whether the failure is the primary's, which has a different repair. */
  primaryFailed?: boolean
  /** Whether that primary is the module's only file, which decides which repair it has. */
  primaryIsOnlyFile?: boolean
  allReady: boolean
}

export function getStepsReadiness(steps: StepWithValidation[], primaryStepId?: string | null): StepReadiness {
  const counts = { failed: 0, working: 0, unreviewed: 0, unknown: 0 }
  let primaryFailed = false
  for (const step of steps) {
    const reason = getStepBlockReason(step)
    if (reason) counts[reason] += 1
    if (reason === 'failed' && !!primaryStepId && step.stepId === primaryStepId) primaryFailed = true
  }
  return {
    ...counts,
    primaryFailed,
    primaryIsOnlyFile: steps.length === 1,
    allReady: steps.length > 0 && counts.failed + counts.working + counts.unreviewed + counts.unknown === 0,
  }
}

/**
 * What to tell an expert whose primary file failed. The repair depends on whether the
 * module has other files, because decision 7 only allows deleting a failed primary when it
 * is the only one: with siblings the route is Change, and deleting would hand the lead slot
 * to a file nobody chose; alone, Delete empties the slot and Add Primary File comes back.
 *
 * Shared so the Delivery Mode alert, the generate gate and the row tooltip cannot offer
 * different repairs for the same file — and so none of them can offer one that isn't there.
 * "Upload it again" was in two of them for a case with no upload route at all.
 */
export function primaryFailureRepair(hasOtherFiles: boolean): string {
  return hasOtherFiles ? 'Use Change to pick another file to lead.' : 'Delete it and upload another.'
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export function getReadinessMessage(readiness: StepReadiness, opts?: { statusesUnavailable?: boolean }): string | null {
  // The primary is swapped or re-uploaded, never removed — decision 7 disables its Delete.
  // Telling the expert to remove it pointed them at a button they cannot press, and at the
  // one repair that silently hands the lead slot to a supplementary file.
  if (readiness.primaryFailed) {
    return `Your Primary File failed to process. ${primaryFailureRepair(!readiness.primaryIsOnlyFile)}`
  }
  if (readiness.failed > 0) {
    return `Remove ${readiness.failed === 1 ? 'the file that failed' : `the ${readiness.failed} files that failed`} to process before generating.`
  }
  if (readiness.working > 0) return `Waiting for ${plural(readiness.working, 'file')} to finish processing.`
  if (readiness.unreviewed > 0)
    return `${plural(readiness.unreviewed, 'file')} still ${readiness.unreviewed === 1 ? 'needs' : 'need'} review.`
  // Last, because the blockers above are ones the expert can act on now. A failed snapshot
  // leaves every finished file here, so this is what the button says for the rest of the
  // session — it has to name the real reason and point at the retry, not invent processing.
  if (readiness.unknown > 0) {
    return opts?.statusesUnavailable
      ? `File statuses couldn't be loaded, so ${plural(readiness.unknown, 'file')} can't be confirmed ready. Retry above.`
      : `Checking ${plural(readiness.unknown, 'file')}…`
  }
  return null
}

/**
 * Whether a file's Delete action is available — decision 7.
 *
 * The primary is swapped, not deleted, so the module always leads with something. A
 * primary that failed to process is the exception: it cannot be swapped in usefully and
 * has to be removable so the expert can upload a replacement.
 *
 * The exception is narrowed to the only-file case on purpose. Deleting a failed primary
 * that has siblings does not empty the slot — the backend promotes the first remaining file
 * (`_ensure_primary_step_id`), which keeps the access it had as a supplementary file and
 * gets no delivery-mode correction, unlike a swap (decision 29). A promoted Media Only file
 * leads a module from outside RAG with nothing in the UI saying so, since the primary table
 * has no Content Access column. Change is the route whenever there is something to change
 * to, and it persists both corrections; alone, Delete is safe because nothing is promoted.
 *
 * No expert is stranded: supplementary files are always deletable, so a module whose other
 * files are all broken can be reduced to the only-file case and then re-uploaded.
 */
export function canDeleteStep({
  isPrimary,
  hasError,
  isOnlyFile,
}: {
  isPrimary: boolean
  hasError: boolean
  isOnlyFile: boolean
}): boolean {
  if (!isPrimary) return true
  return hasError && isOnlyFile
}
